import {
  AnalyticEvent,
  ReservationVmFragment,
  formatDraftReservationDate,
  formatPrice,
  formatReservationOffset,
  isWeb,
  upperFirst,
  useAuthorizePaymentMutation,
  useCompleteRescheduleMutation,
  useConfirmPayment,
  useDeclareRescheduleReservationMutation,
  useEstimateProcessingFeeQuery,
  useEstimateReservationRescheduleQuery,
  usePostHog,
  useServiceCalendar,
} from "@stepapp/core";
import { PaymentStatus } from "@stepapp/core/apiTypes";
import { captureSentryException } from "@stepapp/sentry";
import { Block, SvgIcon, Typography, palette, spacing } from "@stepapp/ui";
import { format, isSameHour } from "date-fns";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { StyleSheet } from "react-native";
import ServiceWeekCalendar from "../../components/Calendar/ServiceWeekCalendar";
import { useAppSelector } from "../../hooks";
import { AppStorage } from "../../services/AppStorage";
import EditContentContainer from "./EditContentContainer";

type StatusType = "success" | "loading" | "";

type EditReservationDateProps = {
  setDetailsStatus: (v: StatusType) => void;
  setNewReservationId: (v: string) => void;
  setShowErrorModal: (v: string | undefined) => void;
  reservationId: string;
  handleGoBack: () => void;
  reservation?: ReservationVmFragment;
};

const EditReservationDate = ({
  setDetailsStatus,
  setNewReservationId,
  setShowErrorModal,
  reservationId,
  handleGoBack,
  reservation,
}: EditReservationDateProps) => {
  const postHog = usePostHog();
  const { t } = useTranslation();
  const priceFormatter = formatPrice(AppStorage().get("countryCode"));

  const reservationDate = formatReservationOffset(
    reservation?.startDate || "",
    reservation?.subscription?.startDate,
  );

  const calendarDate = reservationDate ? format(reservationDate, "yyyy-MM-dd'T'HH:mm:ssXXX") : "";

  const [newStartDate, setNewStartDate] = useState<string | undefined>(undefined);
  const { data: processingFee } = useEstimateProcessingFeeQuery({
    variables: { reservationId },
  });

  const parsedDate = formatDraftReservationDate(newStartDate, reservation?.subscription?.startDate);

  const { data: reservationRescheduleData, loading } = useEstimateReservationRescheduleQuery({
    notifyOnNetworkStatusChange: true,
    variables: {
      reservationId,
      data: { startDate: parsedDate },
    },
    skip: !parsedDate,
  });
  const [completeReschedule] = useCompleteRescheduleMutation();
  const [declareRescheduleReservation] = useDeclareRescheduleReservationMutation();
  const [authorizePayment] = useAuthorizePaymentMutation();
  const stripeConfirmPayment = useConfirmPayment();

  const { startDate: calendarStartDate, endDate: calendarEndDate } = useAppSelector(
    ({ calendar }) => calendar,
  );

  const {
    selectedDate,
    hours,
    handleDayPress,
    handleHourPress,
    markedDates,
    selectedHour,
    loading: calendarLoading,
    resetDateAndHour,
    refetchCalendar,
  } = useServiceCalendar({
    bundleSlug: reservation?.bundle?.slug || "",
    timeConsumption: reservation?.timeConsumption || "",
    locationId: reservation?.locationId || "",
    date: calendarDate,
    workerId: reservation?.workers?.[0]?.id || undefined,
    calendarStartDate,
    calendarEndDate,
  });

  const canEditWithoutCharge = useMemo(
    () => !processingFee?.estimateProcessingFee?.amount,
    [processingFee?.estimateProcessingFee?.amount],
  );

  const oneTimeService = !reservation?.subscription?.id;
  const editServiceHours = useMemo(() => (oneTimeService ? 48 : 36), [oneTimeService]);

  const parsedSelectedDate = new Date(`${selectedDate} ${selectedHour || "00:00"}`);
  const hasSameHour = isSameHour(parsedSelectedDate, reservationDate);

  const date = useMemo(() => {
    if (!reservationDate) return { day: "", hour: "" };

    const markedDate = selectedDate ? parsedSelectedDate.toISOString() : reservationDate;
    const markedHour = selectedDate ? selectedHour || "" : format(reservationDate, "HH:mm");

    return { day: upperFirst(format(markedDate, "d MMMM")), hour: markedHour };
  }, [reservationDate, parsedSelectedDate, selectedDate, selectedHour]);

  const reservationRescheduleFee = reservationRescheduleData?.estimateReservationReschedule?.amount;
  const isSubmitDisabled = !(selectedDate && selectedHour) || hasSameHour || loading;
  const buttonLabel = `${t("screens.serviceDetails.changeDate2")} ${
    reservationRescheduleFee
      ? t("screens.serviceDetails.fee", {
          fee: `${priceFormatter(reservationRescheduleFee)}`,
        })
      : ""
  }`;

  const handleClose = useCallback(() => {
    resetDateAndHour();
    handleGoBack();
    setDetailsStatus("");
  }, [resetDateAndHour, setDetailsStatus, handleGoBack]);

  const handleSaveChanges = useCallback(async () => {
    postHog?.capture(AnalyticEvent.Reservation.ChangeDate, {
      from: format(reservationDate, "yyyy-MM-dd HH:mm"),
      to: format(parsedSelectedDate, "yyyy-MM-dd HH:mm"),
    });

    setDetailsStatus("loading");

    const { data: declareRescheduleData, errors: declareRescheduleErrors } =
      await declareRescheduleReservation({
        variables: {
          reservationId,
          data: {
            startDate: parsedDate,
          },
        },
      });

    if (declareRescheduleErrors) {
      setDetailsStatus("");
      return setShowErrorModal(declareRescheduleErrors?.[0]?.message);
    }

    const requiresAction =
      declareRescheduleData?.declareRescheduleReservation?.payment?.status ===
      PaymentStatus.RequiresAction;

    async function completeReservationReschedule() {
      const { data: completeSwapData, errors } = await completeReschedule({
        variables: {
          swapId: declareRescheduleData?.declareRescheduleReservation?.id || "",
        },
        refetchQueries: ["GetReservations"],
      });

      if (errors) {
        captureSentryException("Edit reservation date error", {
          extra: {
            errorMessage: errors?.[0]?.message,
            reservationId,
            isSubscription: !oneTimeService,
          },
        });
        setDetailsStatus("");
        return setShowErrorModal(errors?.[0]?.message);
      }

      setNewReservationId(
        completeSwapData ? completeSwapData?.completeReschedule?.toReservation?.id : "",
      );
      setDetailsStatus("success");
      resetDateAndHour();
    }

    // Verification no needed
    if (!requiresAction) {
      completeReservationReschedule();
      return;
    }

    const { data: authorizePaymentData } = await authorizePayment({
      variables: {
        paymentId: declareRescheduleData?.declareRescheduleReservation?.payment?.id || "",
      },
    });
    const authorizationRequired =
      authorizePaymentData?.authorizePayment?.status === PaymentStatus.RequiresAction;

    if (!authorizationRequired) {
      completeReservationReschedule();
      return;
    }

    const confirmStripePayment = await stripeConfirmPayment(
      authorizePaymentData?.authorizePayment?.stripePaymentNextStep?.clientSecret || "",
      declareRescheduleData?.declareRescheduleReservation?.payment?.id || "",
      captureSentryException,
    );

    if (confirmStripePayment) {
      completeReservationReschedule();
    } else {
      setDetailsStatus("");
      setShowErrorModal("confirmPaymentError");
    }
  }, [
    setDetailsStatus,
    setNewReservationId,
    setShowErrorModal,
    declareRescheduleReservation,
    authorizePayment,
    completeReschedule,
    stripeConfirmPayment,
    resetDateAndHour,
    parsedDate,
    reservationId,
    reservationDate,
    parsedSelectedDate,
    postHog,
    oneTimeService,
  ]);

  useEffect(() => {
    if (selectedHour && selectedDate) {
      const newDate = `${selectedDate} ${selectedHour}`;
      setNewStartDate(newDate);
    } else {
      setNewStartDate(undefined);
    }
  }, [selectedDate, selectedHour]);

  useEffect(() => {
    refetchCalendar();
  }, [refetchCalendar]);

  return (
    <EditContentContainer
      questionType="date"
      handleSaveChanges={handleSaveChanges}
      loading={loading}
      handleClose={handleClose}
      isSubmitDisabled={isSubmitDisabled}
      date={date}
      buttonLabel={buttonLabel}
      categoryType={reservation?.bundle?.categoryType}
    >
      <>
        {canEditWithoutCharge && (
          <Block row padding={1.5} style={styles.containerCalendarHeader}>
            <SvgIcon color={palette.secondary.wrongPink} name="info-filled" />
            <Block justifyCenter flex={1} marginLeft={1.5}>
              <Typography type="accent" variant="lightLabel">
                {t("screens.serviceDetails.calendarChangeDateInfo", {
                  hoursBeforeFee: editServiceHours,
                })}
              </Typography>
            </Block>
          </Block>
        )}

        <ServiceWeekCalendar
          hours={hours}
          startDate={calendarDate}
          handleDayPress={handleDayPress}
          handleHourPress={handleHourPress}
          resetSelectedDate={resetDateAndHour}
          markedDates={markedDates}
          selectedDate={selectedDate}
          selectedHour={selectedHour}
          loading={calendarLoading}
          containerStyles={styles.calendar}
        />
      </>
    </EditContentContainer>
  );
};

export default EditReservationDate;

const styles = StyleSheet.create({
  calendar: {
    paddingHorizontal: spacing.unit * 2.5,
    marginBottom: spacing.unit * (isWeb ? 12 : 0),
    marginTop: spacing.unit,
  },
  containerCalendarHeader: {
    backgroundColor: palette.common.wrongPink,
  },
});
